import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import BookDemoModal from '../../Components/BookDemoModal/BookDemoModal';
import { Calendar, Mail, ArrowRight, Sparkles, Layers, Cpu, Globe, ExternalLink } from 'lucide-react';

const CALENDLY_URL = "https://calendly.com/sanjaysoni-provaantech/vendor-empanelment-discussion-provaantech";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const BookDemoPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleCalendlyEvent = async (e) => {
      if (e.origin === "https://calendly.com" && e.data && e.data.event === "calendly.event_scheduled") {
        console.log("Calendly event scheduled:", e.data.payload);
        const payload = e.data.payload || {};
        const invitee = payload.invitee || {};
        const event = payload.event || {};

        const name = invitee.name || (invitee.first_name ? `${invitee.first_name} ${invitee.last_name || ''}`.trim() : 'Calendly Booking');
        const email = invitee.email || 'N/A';
        const date = event.start_time || new Date().toISOString();
        const phone = invitee.text_reminder_number || invitee.phone || 'Calendly Sync';
        const message = `Scheduled directly via Calendly Inline Page`;

        try {
          const res = await fetch(`${API_BASE_URL}/api/v1/demo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name,
              email,
              phone,
              message,
              date,
              turnstileToken: 'calendly-verified-token'
            })
          });

          if (res.ok) {
            toast.success('Calendly booking synced to Admin Dashboard!');
          }
        } catch (err) {
          console.error('Failed to sync Calendly event to backend:', err);
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);
    return () => window.removeEventListener('message', handleCalendlyEvent);
  }, []);

  const embedUrl = `${CALENDLY_URL}?embed_domain=${encodeURIComponent(window.location.hostname || 'localhost')}&embed_type=Inline&primary_color=3b82f6`;

  return (
    <div style={{ minHeight: '90vh', background: 'radial-gradient(ellipse at top, #1e293b 0%, #0f172a 100%)', color: '#f8fafc', padding: '90px 20px 100px', fontFamily: '"Inter", system-ui, sans-serif', position: 'relative', overflow: 'hidden' }}>
      
      {/* Dynamic Background Glow */}
      <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '350px', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(168, 85, 247, 0.08) 50%, transparent 70%)', filter: 'blur(70px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '1160px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        
        {/* Header Hero */}
        <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 64px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 20px', background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#60a5fa', borderRadius: '100px', fontSize: '0.875rem', fontWeight: 600, marginBottom: '24px' }}>
            <Sparkles size={16} /> Enterprise Digital Transformation & AI Solutions
          </div>

          <h1 style={{ fontSize: '3.5rem', fontWeight: 800, lineHeight: 1.12, marginBottom: '24px', letterSpacing: '-0.03em', color: '#ffffff' }}>
            Experience Provaantech <br />
            <span style={{ background: 'linear-gradient(135deg, #60a5fa 0%, #c084fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Solutions in Action
            </span>
          </h1>

          <p style={{ fontSize: '1.2rem', color: '#94a3b8', lineHeight: 1.6, margin: '0 auto' }}>
            Book a 1-on-1 demonstration with our solution architects. Discover how ToxBox AI, ProHirey, GCC Acceleration, and Cloud Engineering drive operational dominance for your enterprise.
          </p>
        </div>

        {/* Action Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '32px', marginBottom: '64px' }}>
          
          {/* Schedule Demo Card */}
          <div 
            style={{ background: 'rgba(30, 41, 59, 0.6)', borderRadius: '28px', padding: '44px', border: '1px solid rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(16px)', display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)', boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.5)' }}
            onClick={() => setIsModalOpen(true)}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(59, 130, 246, 0.25)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.12)'; e.currentTarget.style.boxShadow = '0 20px 40px -15px rgba(0, 0, 0, 0.5)'; }}
          >
            <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.3) 100%)', color: '#60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '28px' }}>
              <Calendar size={30} />
            </div>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#ffffff', marginBottom: '12px', letterSpacing: '-0.01em' }}>Schedule Live Demo</h3>
            <p style={{ color: '#94a3b8', lineHeight: 1.6, marginBottom: '36px', flexGrow: 1, fontSize: '1.025rem' }}>
              Pick a date & time that suits your leadership team. We will present a tailored walkthrough of Provaantech's AI and engineering capabilities.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', color: '#60a5fa', fontWeight: 700, fontSize: '1.1rem', gap: '10px' }}>
              Select Date & Time <ArrowRight size={20} />
            </div>
          </div>

          {/* Email Enterprise Team Card */}
          <div 
            style={{ background: 'rgba(30, 41, 59, 0.6)', borderRadius: '28px', padding: '44px', border: '1px solid rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(16px)', display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)', boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.5)' }}
            onClick={() => window.location.href = 'mailto:contact@provaantech.com?subject=Enterprise Demo & Architectural Inquiry'}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.borderColor = '#c084fc'; e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(168, 85, 247, 0.25)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.12)'; e.currentTarget.style.boxShadow = '0 20px 40px -15px rgba(0, 0, 0, 0.5)'; }}
          >
            <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(147, 51, 234, 0.3) 100%)', color: '#c084fc', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '28px' }}>
              <Mail size={30} />
            </div>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#ffffff', marginBottom: '12px', letterSpacing: '-0.01em' }}>Email Solutions Team</h3>
            <p style={{ color: '#94a3b8', lineHeight: 1.6, marginBottom: '36px', flexGrow: 1, fontSize: '1.025rem' }}>
              Need RFPs, custom enterprise architecture reviews, or specialized compliance consultations? Reach out directly to our principal engineers.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', color: '#c084fc', fontWeight: 700, fontSize: '1.1rem', gap: '10px' }}>
              contact@provaantech.com <ArrowRight size={20} />
            </div>
          </div>

        </div>

        {/* Embedded Calendly Section */}
        <div style={{ background: '#ffffff', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)', marginBottom: '80px', position: 'relative' }}>
          <div style={{ padding: '20px 28px', background: '#0f172a', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Calendar size={22} color="#60a5fa" /> Direct Live Scheduling
              </h3>
              <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>Select your preferred discussion slot below</span>
            </div>
            <a 
              href={CALENDLY_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#60a5fa', borderRadius: '100px', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}
            >
              Open Calendly <ExternalLink size={14} />
            </a>
          </div>
          <iframe
            src={embedUrl}
            width="100%"
            height="700px"
            frameBorder="0"
            title="Provaantech Calendly Scheduler"
            style={{ border: 'none', minHeight: '680px', width: '100%' }}
          />
        </div>

        {/* Platform Pillars */}
        <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '56px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <Cpu color="#38bdf8" size={28} />
            <div>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.05rem' }}>Custom AI Models</div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>ToxBox AI & LLM integration</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <Layers color="#c084fc" size={28} />
            <div>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.05rem' }}>ProHirey Recruitment</div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>AI candidate evaluation</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: 'rgba(255, 255, 255, 0.03)', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <Globe color="#34d399" size={28} />
            <div>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.05rem' }}>GCC Acceleration</div>
              <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>Global center expansion</div>
            </div>
          </div>
        </div>

      </div>

      <BookDemoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BookDemoPage;
